import React, {
  useMemo, useEffect, useState,
} from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { useNavigate } from 'react-router-dom';
import { IoChevronUpOutline, IoChevronDownOutline } from 'react-icons/io5';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  Row,
} from '@tanstack/react-table';
import styles from './fournisseurs-table.module.scss';
import { ICompany } from '~/types/companies';

const columns = [{
  header: 'Nom de la société',
  accessorKey: 'name',
}, {
  header: 'Membres',
  accessorKey: 'usersCount',
  cell: (row : any) => {
    const value = row.getValue() || 0;
    return (
      <span className={styles.usersCount}>
        {value}
      </span>
    );
  },
}, {
  header: 'Nombre d\'offre',
  accessorKey: 'productsCount',
  cell: (row : any) => {
    const value = row.getValue() || 0;
    return (
      <span className={styles.productsCount}>
        {value}
      </span>
    );
  },
}, {
  header: 'Chiffre d\'affaire',
  accessorKey: 'providerTurnover',
  cell: (row : any) => {
    const value = row.getValue() || 0;
    return (
      <span>
        {value.toFixed(2)} €
      </span>
    );
  },
}, {
  header: 'Contact',
  accessorKey: 'contact.firstName',
  cell: ({ row } : any) => (
    <>
      {row.original.contact?.firstName} {row.original.contact?.lastName}
    </>
  ),
}, {
  header: 'Mail',
  accessorKey: 'contact.email',
}];

const FournisseursTable = (
  {
    companies,
    handleSorting,
  } : {
    companies: ICompany[],
    handleSorting: any,
  },
) => {
  const isTablet = useMediaQuery('(max-width: 1280px)');
  const isMobile = useMediaQuery('(max-width: 900px)');

  const [sorting, setSorting] = useState<any>([]);
  const navigate = useNavigate();

  useEffect(() => {
    handleSorting(sorting[0]);
  }, [sorting]);

  let tableColumns = columns;
  if (isTablet) tableColumns = columns.slice(0, 3);
  if (isMobile) {
    tableColumns = [
      columns[0],
      {
        ...columns[1],
        header: 'Nombre d\'offre',
      },
    ];
  }

  const data = useMemo(() => companies, [companies]);
  const table = useReactTable({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  const onRowClick = (row: Row<ICompany>) => {
    navigate(`/fournisseurs/${row.original._id}/membres`);
  };

  return (
    <div className={styles.fournisseursTable}>
      <table>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={styles[header.column.id]}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                  <div className={styles.sortIcons}>
                    <span className={header.column.getIsSorted() === 'asc' ? styles.active : ''}><IoChevronUpOutline /></span>
                    <span className={header.column.getIsSorted() === 'desc' ? styles.active : ''}><IoChevronDownOutline /></span>
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} onClick={() => onRowClick(row)}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className={styles[cell.column.id]}>
                  <p>
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext(),
                    )}
                  </p>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default FournisseursTable;
